import { MicOff, Search } from "@scoach/ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";

import { useLiveMeetingStore } from "../store.ts";
import { TranscriptLine } from "./TranscriptLine.tsx";

type Filter = "all" | "client" | "rep";

/**
 * The transcript body on its own: search + speaker filter, the scrolling list
 * of final lines, the live-partial caret, and the muted/idle/listening empty
 * states. Extracted from TranscriptPanel so the interview right-panel can render
 * a transcript tab without pulling in the sales tab bar / What's New / Use Cases.
 */
export function TranscriptView() {
  const transcript = useLiveMeetingStore((s) => s.transcript);
  const lang = useLiveMeetingStore((s) => s.langMode);
  const muted = useLiveMeetingStore((s) => s.muted);
  const listening = useLiveMeetingStore((s) => s.listening);
  const sttError = useLiveMeetingStore((s) => s.sttError);
  const audioWarning = useLiveMeetingStore((s) => s.audioWarning);
  const livePartial = useLiveMeetingStore((s) => s.livePartial);

  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript.length]);

  const visible = useMemo(
    () =>
      transcript.filter((l) => {
        if (filter === "client" && l.speaker !== "client") return false;
        if (filter === "rep" && l.speaker !== "rep") return false;
        if (search) {
          const q = search.toLowerCase();
          if (!`${l.text} ${l.trans ?? ""}`.toLowerCase().includes(q)) return false;
        }
        return true;
      }),
    [transcript, filter, search],
  );

  return (
    <>
      <div className="panel-actions">
        <div className="search-box">
          <Search size={14} />
          <input
            type="search"
            placeholder="Search transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="seg seg-sm">
          <button
            type="button"
            className={filter === "all" ? "on" : ""}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            type="button"
            className={filter === "client" ? "on" : ""}
            onClick={() => setFilter("client")}
          >
            Client
          </button>
          <button
            type="button"
            className={filter === "rep" ? "on" : ""}
            onClick={() => setFilter("rep")}
          >
            You
          </button>
        </div>
      </div>

      {sttError && (
        <div
          style={{
            padding: "8px 12px",
            margin: "8px 12px 0",
            borderRadius: 6,
            background: "var(--gc-red-50, #fdecea)",
            color: "var(--gc-red, #b00020)",
            fontSize: 13,
            border: "1px solid var(--gc-red, #b00020)",
          }}
        >
          Transcription unavailable: {sttError}
        </div>
      )}

      {!sttError && audioWarning && (
        <div
          role="status"
          style={{
            padding: "8px 12px",
            margin: "8px 12px 0",
            borderRadius: 6,
            background: "var(--gc-yellow-50, #fef7e0)",
            color: "var(--gc-yellow-900, #7a5900)",
            fontSize: 13,
            border: "1px solid var(--gc-yellow-700, #f9ab00)",
          }}
        >
          ⚠️ {audioWarning}
        </div>
      )}

      <div className="transcript-scroll scroll" ref={scrollRef}>
        {visible.map((l) => (
          <TranscriptLine key={l.id} line={l} lang={lang} />
        ))}

        {livePartial && (
          <div
            className="live-partial"
            title="Live partial transcript — being revised in real time"
          >
            <span className="live-partial-text">{livePartial}</span>
            <span className="live-caret" />
          </div>
        )}

        {muted ? (
          <div className="t-muted-row">
            <MicOff size={14} /> Listening paused — hint generation suspended. Audio still captured
            locally.
          </div>
        ) : !listening ? (
          <div className="t-typing">
            <div className="t-typing-bubble">
              <span className="t-typing-label" style={{ color: "var(--text-3)" }}>
                Take notes here. Live transcription is optional — enable it from the left rail any
                time.
              </span>
            </div>
          </div>
        ) : transcript.length === 0 ? (
          <div className="t-typing">
            <div className="t-typing-bubble">
              <span className="dot dot-pulse" style={{ background: "var(--text-3)" }} />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--text-3)", animationDelay: "150ms" }}
              />
              <span
                className="dot dot-pulse"
                style={{ background: "var(--text-3)", animationDelay: "300ms" }}
              />
              <span className="t-typing-label">Listening for speech…</span>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
